<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">

        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">Создать новую группу</h4>
            </div>
            <div class="card-body">

              <div v-if="successMessage" class="alert alert-success">
                <span>{{ successMessage }}</span>
                <button type="button" class="close" @click="successMessage = null">×</button>
              </div>

              <div v-if="errorMessage" class="alert alert-danger">
                <span>{{ errorMessage }}</span>
                <button type="button" class="close" @click="errorMessage = null">×</button>
              </div>

              <form @submit.prevent="createGroup">
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Название группы</label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="Например: B-205"
                        v-model="form.name"
                        required
                      >
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Уровень образования (Level)</label>
                      <select class="form-control" v-model="form.groupLevel" required>
                        <option :value="1">Бакалавр (1)</option>
                        <option :value="2">Специалист (2)</option>
                        <option :value="3">Магистр (3)</option>
                        <option :value="4">PhD (4)</option>
                      </select>
                    </div>
                  </div>

                  <div class="col-md-2">
                    <div class="form-group">
                      <label>Курс</label>
                      <input
                        type="number"
                        class="form-control"
                        placeholder="1"
                        min="1"
                        max="6"
                        v-model="form.groupCourse"
                        required
                      >
                    </div>
                  </div>

                  <div class="col-md-2 d-flex align-items-end">
                    <button type="submit" class="btn btn-info btn-fill btn-block mb-3" :disabled="loading">
                      {{ loading ? '...' : 'Создать' }}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div class="col-md-12">
          <div class="card">

            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h4 class="card-title">Список групп</h4>
                <p class="category">Все учебные группы</p>
              </div>

              <div class="pagination-controls" v-if="groups.length > 0 || pageIndex > 1">
                 <button
                   class="btn btn-sm btn-secondary btn-fill"
                   :disabled="pageIndex === 1 || fetching"
                   @click="changePage(-1)"
                 >
                   <i class="fa fa-chevron-left"></i> Назад
                 </button>

                 <span class="mx-3 font-weight-bold">Стр. {{ pageIndex }}</span>

                 <button
                   class="btn btn-sm btn-secondary btn-fill"
                   :disabled="isLastPage || fetching"
                   @click="changePage(1)"
                 >
                   Вперед <i class="fa fa-chevron-right"></i>
                 </button>
              </div>
            </div>

            <div class="card-body table-full-width table-responsive">
              <table class="table table-hover table-striped">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Название</th>
                    <th>Уровень</th>
                    <th>Курс</th>
                    <th>Дата создания</th>
                    <th class="text-right">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="fetching">
                    <td colspan="6" class="text-center">Загрузка списка...</td>
                  </tr>
                  <tr v-if="groups.length === 0 && !fetching">
                    <td colspan="6" class="text-center">Список пуст</td>
                  </tr>
                  <tr v-for="(group, index) in groups" :key="group.id">
                    <td>{{ index + 1 }}</td>
                    <td><strong>{{ group.name }}</strong></td>
                    <td>{{ getLevelName(group.groupLevel) }}</td>
                    <td>{{ group.groupCourse }} курс</td>
                    <td>{{ formatDate(group.createdAt) }}</td>
                    <td class="text-right">
                      <button
                        class="btn btn-danger btn-sm btn-fill"
                        @click="deleteGroup(group.id)"
                        title="Удалить"
                      >
                        <i class="fa fa-trash"></i> Удалить
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="text-muted small p-3" v-if="groups.length > 0">
                 Показано {{ groups.length }} записей (Лимит: {{ pageSize }})
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../server';

export default {
  name: 'GroupManager',
  data() {
    return {
      // Create Form Data
      form: {
        name: '',
        groupLevel: 1,
        groupCourse: 1
      },
      loading: false,

      // List Data
      groups: [],
      fetching: false,

      // Pagination Data
      pageIndex: 1,
      pageSize: 50,
      isLastPage: false,

      // Messages
      errorMessage: null,
      successMessage: null,
    };
  },
  mounted() {
    this.fetchGroups();
  },
  methods: {
    // 1. GET ALL GROUPS (With Pagination)
    async fetchGroups() {
      this.fetching = true;
      try {
        const response = await axios.get(API_URL + '/api/group', {
          params: {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize
          }
        });

        if (response.data && response.data.code === 200) {
          const data = response.data.data || [];
          this.groups = data;
          // Determine if we are on the last page
          this.isLastPage = data.length < this.pageSize;
        } else {
          this.groups = [];
        }
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        this.fetching = false;
      }
    },

    // 2. Change Page
    changePage(direction) {
      this.pageIndex += direction;
      this.fetchGroups();
    },

    // 3. CREATE GROUP
    async createGroup() {
      this.loading = true;
      this.errorMessage = null;
      this.successMessage = null;

      const token = localStorage.getItem('token');

      if (!token) {
        this.errorMessage = 'Ошибка авторизации.';
        this.loading = false;
        return;
      }

      try {
        const payload = {
          name: this.form.name,
          groupLevel: parseInt(this.form.groupLevel),
          groupCourse: parseInt(this.form.groupCourse)
        };

        const response = await axios.post(
          API_URL + '/api/group',
          payload,
          {
            headers: {
              'accept': '*/*',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.code === 200) {
          this.successMessage = `Группа "${this.form.name}" успешно создана!`;
          this.form.name = '';
          // Reset to page 1 to see the new group
          this.pageIndex = 1;
          this.fetchGroups();
        } else {
          this.errorMessage = 'Не удалось создать группу.';
        }

      } catch (error) {
        console.error('Create Error:', error);
        if (error.response && error.response.status === 401) {
           this.errorMessage = 'Сессия истекла.';
        } else {
           this.errorMessage = 'Ошибка сервера при создании.';
        }
      } finally {
        this.loading = false;
      }
    },

    // 4. DELETE GROUP
    async deleteGroup(id) {
      if (!confirm('Вы уверены, что хотите удалить эту группу?')) return;

      const token = localStorage.getItem('token');

      try {
        const response = await axios.delete(
          API_URL + `/api/group/${id}`,
          {
            headers: {
              'accept': '*/*',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data && (response.data.code === 200 || response.status === 200)) {
           this.successMessage = 'Группа успешно удалена.';
           this.fetchGroups(); // Refresh current page
        } else {
           this.errorMessage = 'Не удалось удалить группу.';
        }

      } catch (error) {
        console.error('Delete Error:', error);
        this.errorMessage = 'Ошибка при удалении группы.';
      }
    },

    // Helpers
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('ru-RU');
    },
    getLevelName(levelId) {
      const levels = {
        1: 'Бакалавр',
        2: 'Специалист',
        3: 'Магистр',
        4: 'Докторантура'
      };
      return levels[levelId] || `Неизвестно (${levelId})`;
    }
  }
};
</script>

<style scoped>
.mb-3 { margin-bottom: 1rem; }
.btn-block { width: 100%; display: block; }
.close { float: right; font-size: 1.5rem; font-weight: 700; line-height: 1; color: #000; text-shadow: 0 1px 0 #fff; opacity: .5; background: transparent; border: 0; cursor: pointer;}
.pagination-controls { display: flex; align-items: center; }
.mx-3 { margin-left: 1rem; margin-right: 1rem; }
</style>
